import type { TimingDirection, TimingGrade } from './judge/Judgement.js';

/**
 * What the simulation reports each frame.
 *
 * The simulation has no side effects of its own: it does not play sounds, emit
 * particles or touch a renderer. It mutates its state and appends to this queue,
 * and the runtime distributes the results. That is what keeps the layer testable
 * in Node and what stops presentation concerns leaking back into gameplay — the
 * pattern the Unity build could not follow, because a wave *was* a scene object.
 */

/**
 * One wave leaving play with a grade attached — the phase's main output.
 *
 * Carries the wave's position and colour rather than a reference to it, because
 * wave records are pooled and the slot this came from will be another wave
 * before a consumer gets round to reading it. It also carries everything a
 * later "successful waves accumulate into the central mandala" step would need,
 * so that step can subscribe here rather than reaching into the simulation.
 */
export interface WaveResolvedEvent {
  readonly type: 'waveResolved';
  readonly waveId: number;
  readonly eventIndex: number;
  readonly grade: TimingGrade;
  readonly timingOffsetMs: number;
  readonly timingDirection: TimingDirection;
  readonly scoreAwarded: number;
  readonly combo: number;
  /** False when the wave expired untouched, which is the only way to score a miss. */
  readonly tapped: boolean;
  readonly songTime: number;
  readonly bestTime: number;
  readonly originX: number;
  readonly originY: number;
  readonly radius: number;
  /** Failed taps this wave survived before it was cleared. */
  readonly badAttempts: number;
  /** The wave's authored `dangerColor`, copied out of the pooled record. */
  readonly color: readonly [number, number, number];
}

/**
 * A failed tap that left the wave standing.
 *
 * Distinct from a resolution on purpose. A wave emits any number of these and
 * exactly one `WaveResolvedEvent`, so a consumer counting resolutions still
 * counts waves, while a consumer counting attempts can see how hard they were
 * fought for.
 */
export interface WaveAttemptEvent {
  readonly type: 'waveAttempt';
  readonly waveId: number;
  readonly eventIndex: number;
  readonly grade: 'bad';
  readonly timingOffsetMs: number;
  readonly timingDirection: TimingDirection;
  readonly attempt: number;
  readonly songTime: number;
  readonly bestTime: number;
}

export type SimulationEvent =
  | {
      readonly type: 'waveSpawned';
      readonly waveId: number;
      readonly eventIndex: number;
      /** The event's authored time. */
      readonly chartTime: number;
      /** Song time the scheduler actually reached when it fired. */
      readonly songTime: number;
    }
  | {
      readonly type: 'waveRetired';
      readonly waveId: number;
      readonly eventIndex: number;
      readonly songTime: number;
      /** `resolved` once judging exists; `expired` only if nothing judged it. */
      readonly reason: 'expired' | 'resolved';
    }
  | WaveResolvedEvent
  | WaveAttemptEvent
  | {
      /** The cap was reached, so this event produced no wave. */
      readonly type: 'waveDropped';
      readonly eventIndex: number;
      readonly chartTime: number;
      readonly songTime: number;
    }
  | {
      readonly type: 'chartExhausted';
      readonly songTime: number;
    };

/**
 * A fixed-capacity queue the simulation appends to and the runtime drains.
 *
 * Bounded on purpose: an unbounded array would grow without limit if a caller
 * ever forgot to drain it, and silently dropping the oldest is preferable to
 * leaking for the length of a song.
 */
export class SimulationEventQueue {
  private readonly buffer: SimulationEvent[] = [];
  private overflowed = 0;

  constructor(private readonly capacity = 512) {}

  get length(): number {
    return this.buffer.length;
  }

  /** Events discarded because the queue was full and nobody drained it. */
  get overflowCount(): number {
    return this.overflowed;
  }

  push(event: SimulationEvent): void {
    if (this.buffer.length >= this.capacity) {
      this.buffer.shift();
      this.overflowed += 1;
    }
    this.buffer.push(event);
  }

  /** Returns everything queued and empties the queue. */
  drain(): SimulationEvent[] {
    if (this.buffer.length === 0) return [];
    return this.buffer.splice(0, this.buffer.length);
  }

  clear(): void {
    this.buffer.length = 0;
    this.overflowed = 0;
  }
}
